import React, { useState } from "react";
import "./contact.css";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Mail, User, Phone, MessageSquare, Send, CheckCircle2, HelpCircle } from "lucide-react";
import { notify } from "../../services/notify.js";

const contactTypes = [
  "Dúvida sobre o site",
  "Problemas com acesso",
  "Assinatura",
  "Outro",
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Aceita telefones internacionais: opcional "+", dígitos, espaços, traços,
// parênteses, pontos e barras. Ex.: +55 (11) 91234-5678, +1 415-555-0132, 020 7946 0958.
const phoneFormatRegex = /^\+?[\d\s().\-/]+$/;

function isValidPhone(value) {
  const trimmed = value.trim();
  if (!phoneFormatRegex.test(trimmed)) return false;
  // Conta apenas os dígitos e valida o intervalo do padrão E.164 (mín. nacional).
  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    contactType: "",
    message: "",
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Informe seu nome.";
    } else if (form.name.trim().length < 3) {
      newErrors.name = "O nome deve ter pelo menos 3 caracteres.";
    }

    if (!form.email.trim()) {
      newErrors.email = "Informe seu e-mail.";
    } else if (!emailRegex.test(form.email.trim())) {
      newErrors.email = "Informe um e-mail válido.";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Informe seu telefone.";
    } else if (!isValidPhone(form.phone)) {
      newErrors.phone = "Informe um telefone válido (com DDD ou código do país).";
    }

    if (!form.contactType) {
      newErrors.contactType = "Selecione o tipo de contato.";
    }

    if (!form.message.trim()) {
      newErrors.message = "Escreva sua mensagem.";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "A mensagem deve ter pelo menos 10 caracteres.";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newErrors = validate();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const isBot = form.website.trim() !== "";

    try {
      if (!isBot) {
        await notify(
          `CONTATO: ${form.contactType}`,
          `Novo contato pelo formulário do site.\n` +
            `Nome: ${form.name}\n` +
            `E-mail: ${form.email}\n` +
            `Telefone: ${form.phone}\n` +
            `Tipo: ${form.contactType}\n` +
            `Mensagem: ${form.message}`
        );
      }
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="de-contact">
      {/* HERO */}
      <section className="de-contact-hero">
        <div className="de-contact-hero-inner">
          <h1 className="de-contact-title">
            Fale com o <span>Domínio Elétrico</span>
          </h1>
          <p className="de-contact-subtitle">
            Tem alguma dúvida sobre o curso ou o site? Problemas com acesso ou
            assinatura? Preencha o formulário abaixo que responderemos assim que possível.
          </p>
        </div>
      </section>

      {/* FORMULÁRIO */}
      <section className="de-contact-section">
        <div className="de-contact-card">
          {submitted ? (
            <div className="de-contact-success">
              <CheckCircle2 size={56} className="de-contact-success-icon" />
              <h2>Mensagem enviada!</h2>
              <p>
                Agradecemos pelo contato. Fique atento ao seu email e WhatsApp porque alguém de nossa equipe poderá entrar em contato por um desses meios.
              </p>
            </div>
          ) : (
            <form className="de-contact-form" onSubmit={handleSubmit} noValidate>
              <div className="de-contact-form-header">
                <span className="de-contact-form-icon">
                  <HelpCircle size={26} />
                </span>
                <h2>Envie sua mensagem</h2>
              </div>

              <div className="de-contact-field">
                <TextField
                  label="Nome completo"
                  variant="outlined"
                  fullWidth
                  value={form.name}
                  onChange={handleChange("name")}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  InputProps={{
                    startAdornment: (
                      <User size={18} className="de-contact-adornment" />
                    ),
                  }}
                />
              </div>

              <div className="de-contact-field">
                <TextField
                  label="E-mail"
                  type="email"
                  variant="outlined"
                  fullWidth
                  value={form.email}
                  onChange={handleChange("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <Mail size={18} className="de-contact-adornment" />
                    ),
                  }}
                />
              </div>

              <div className="de-contact-field">
                <TextField
                  label="Telefone com DDD"
                  variant="outlined"
                  fullWidth
                  value={form.phone}
                  onChange={handleChange("phone")}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  InputProps={{
                    startAdornment: (
                      <Phone size={18} className="de-contact-adornment" />
                    ),
                  }}
                />
              </div>

              <div className="de-contact-field">
                <FormControl fullWidth error={Boolean(errors.contactType)}>
                  <InputLabel id="de-contact-type-label">
                    Tipo de contato
                  </InputLabel>
                  <Select
                    labelId="de-contact-type-label"
                    label="Tipo de contato"
                    value={form.contactType}
                    onChange={handleChange("contactType")}
                  >
                    {contactTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.contactType && (
                    <FormHelperText>{errors.contactType}</FormHelperText>
                  )}
                </FormControl>
              </div>

              <div className="de-contact-field">
                <TextField
                  label="Mensagem"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={5}
                  value={form.message}
                  onChange={handleChange("message")}
                  error={Boolean(errors.message)}
                  helperText={errors.message}
                  InputProps={{
                    startAdornment: (
                      <MessageSquare
                        size={18}
                        className="de-contact-adornment de-contact-adornment-top"
                      />
                    ),
                  }}
                />
              </div>

              {/* Honeypot */}
              <div className="de-contact-hp" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange("website")}
                />
              </div>

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                className="de-contact-submit"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Send size={18} />
                  )
                }
              >
                {isSubmitting ? "Enviando..." : "Enviar mensagem"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

export default Contact;
